import React, {Component, createContext, useContext} from 'react'
import {
  ChakraProvider,
  useToast,
} from '@chakra-ui/react';
import customTheme from './theme/theme';

import LandingPage from './subsystems/LandingPage/LandingPage'
import SignIn from './subsystems/Authentication/Authentication'
import PasswordResetRequest from './subsystems/Authentication/PasswordResetRequest'
import PasswordResetConfirm from './subsystems/Authentication/PasswordResetConfirm'
import SuggestieIndienen from './subsystems/Main/SuggestieIndienen/SuggestieIndienen'
import IngediendeSuggesties from './subsystems/Main/IngediendeSuggesties/IngediendeSuggesties'
import AlleSuggesties from './subsystems/Main/AlleSuggesties/AlleSuggesties'
import Overzicht from './subsystems/Main/Overzicht/Overzicht'
import SuggestionInfo from './subsystems/Main/SuggestionInfo/SuggestionInfo'
import SummaryInfo from './subsystems/Main/SummaryInfo/SummaryInfo'
import ProblemInfo from './subsystems/Main/ProblemInfo/ProblemInfo'
import BurgerWelcome from './subsystems/Main/BurgerWelcome/BurgerWelcome'
import VertegenwoordigerWelcome from './subsystems/Main/VertegenwoordigerWelcome/VertegenwoordigerWelcome'
import GuestWelcome from './subsystems/Main/GuestWelcome/GuestWelcome'
import Dashboard from './subsystems/Main/Dashboard/Dashboard'
import Instellingen from './subsystems/Main/Instellingen/Instellingen'
import BerichtSturen from './subsystems/Main/BerichtSturen/BerichtSturen'
import Berichten from './subsystems/Main/Berichten/Berichten'
import BerichtInfo from './subsystems/Main/BerichtInfo/BerichtInfo'
import PrivacyPolicy from './subsystems/PrivacyPolicy/PrivacyPolicy'
import TermsOfService from './subsystems/TermsOfService/TermsOfService'
import CookieConsent from './components/CookieConsent/CookieConsent'
import FeedbackModal from './components/FeedbackModal'
import AiConsentModal from './components/AiConsentModal'
import OfflineBanner from './components/OfflineBanner'

// API URL from environment variable
// In development, use empty string to leverage the proxy in package.json
// In production, REACT_APP_API_URL should be set to the backend URL
const API_URL = process.env.REACT_APP_API_URL || '';

// Create a context for toast functionality
const ToastContext = createContext();

// Toast provider component
function ToastProvider({ children }) {
  const toast = useToast();
  return (
    <ToastContext.Provider value={toast}>
      {children}
    </ToastContext.Provider>
  );
}

// Hook to use toast in class components
export const useToastContext = () => useContext(ToastContext);

// HOC to inject toast into class components
function withToast(WrappedComponent) {
  return function ToastWrapper(props) {
    const toast = useToastContext();
    return <WrappedComponent {...props} toast={toast} />;
  };
}

function App() {
  return (
    <ChakraProvider theme={customTheme}>
      <ToastProvider>
        <AppUIWithToast/>
      </ToastProvider>
    </ChakraProvider>
  );
}

const initialPolicyDetails = {
  policyName: "",
  policyDescription: ""
}

const initialComment = {
  commenting: false,
  text: ""
}

const initialPolicyInfo = {
  policy: initialPolicyDetails,
  comment: initialComment,
  comments: [],
  page: 0
}

// Detect subdomain and set initial route
const getInitialRoute = () => {
  const hostname = window.location.hostname;
  // Check if we're on NAME.metdemos.app subdomain
  if (hostname.startsWith('utrechtnu.')) {
    return 'login';  // Skip landing page for NAME subdomain
  }
  return 'login';  // Default to landing page
};

const initialState = {
  username: '',
  email: '',
  password: '',
  agreedToTerms: false,
  ageVerified: false,
  route: getInitialRoute(),  // Dynamic initial route based on subdomain
  userVersion: 'burger',
  csrfToken: null,
  onboardingCompleted: true,  // Default to true, will be set from backend on login
  feed: {
    feedItems: {},
    page: 0
  },
  policyDetails: initialPolicyDetails,
  policyInfo: initialPolicyInfo,
  suggestionText: '',
  allowAiSummarization: false,  // AI consent for guest submissions
  suggestions: {
    items: [],
    page: 0,
    hasNext: false,
    hasPrev: false,
    total: 0
  },
  allSuggestions: {
    items: [],
    page: 0,
    hasNext: false,
    hasPrev: false,
    total: 0
  },
  summaries: {
    items: [],
    loading: false,
    lastUpdated: null
  },
  trendingTopics: {
    items: [],
    loading: false,
    lastUpdated: null
  },
  problems: {
    items: [],
    loading: false,
    lastUpdated: null
  },
  selectedSuggestion: null,
  selectedSummary: null,
  selectedProblem: null,
  selectedLaw: null,
  selectedReport: null,
  trendingTopicFilter: null,
  messageText: '',
  messageSubject: '',
  messageRecipient: '',
  messageContext: null, // Store suggestion context for messages
  messages: {
    received: {
      items: [],
      page: 0,
      hasNext: false,
      hasPrev: false,
      total: 0
    },
    sent: {
      items: [],
      page: 0,
      hasNext: false,
      hasPrev: false,
      total: 0
    }
  },
  selectedMessage: null,
  showSentTab: false, // Flag to show sent tab after sending a message
  lastMessageTab: 'received', // Track which tab user was on when viewing a message
  isFeedbackModalOpen: false,
  isAiConsentModalOpen: false,
  hasSeenAiConsentModal: false, // Track if user has seen the modal this session
  // Loading states
  isLoggingIn: false,
  isRegistering: false,
  isSubmittingSuggestion: false,
  isLoadingSuggestions: false
}

class AppUI extends Component {

  constructor(props) {
    super()
    this.state = initialState
  }

  componentDidMount() {
    // CSRF token will be fetched lazily when needed (for authenticated operations)
  }

  fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${API_URL}/csrf-token`, {
        method: 'GET',
        credentials: 'include'  // Important: include cookies
      });
      const data = await response.json();
      if (data.csrf_token) {
        // Return a promise that resolves when state is updated
        return new Promise((resolve) => {
          this.setState({ csrfToken: data.csrf_token }, () => {
            resolve(data.csrf_token);
          });
        });
      }
    } catch (error) {
      console.error('Failed to fetch CSRF token:', error);
      return null;
    }
  }

  // Helper function to make authenticated requests with CSRF protection
  authenticatedFetch = async (url, options = {}) => {
    // Ensure we have a CSRF token
    if (!this.state.csrfToken) {
      await this.fetchCsrfToken();
    }

    // Add CSRF token and credentials to options
    const authOptions = {
      ...options,
      credentials: 'include',
      headers: {
        ...options.headers,
        'X-CSRFToken': this.state.csrfToken
      }
    };

    const response = await fetch(url, authOptions);

    // If we get a 400 error (CSRF token expired), refresh token and retry
    if (response.status === 400) {
      const responseClone = response.clone();
      const data = await responseClone.json();
      if (data.message && data.message.includes('Beveiligingstoken')) {
        const newToken = await this.fetchCsrfToken();
        // Retry with new token - rebuild authOptions with fresh token
        const retryOptions = {
          ...options,
          credentials: 'include',
          headers: {
            ...options.headers,
            'X-CSRFToken': newToken || this.state.csrfToken
          }
        };
        return fetch(url, retryOptions);
      }
    }

    return response;
  }

  onNameChange = (value) => {
      this.setState({
        username: value
      })
  }

  onEmailChange = (value) => {
    this.setState({
      email: value
    })
}

  onPasswordChange = (value) => {
    this.setState({
      password: value
    })
  }

  onTermsChange = (value) => {
    this.setState({
      agreedToTerms: value
    })
  }

  onAgeVerifiedChange = (value) => {
    this.setState({
      ageVerified: value
    })
  }

  onRouteChange = (dest) => {
    // Clear showSentTab flag when navigating away from berichten
    const updates = { route: dest };
    if (dest !== 'berichten' && this.state.showSentTab) {
      updates.showSentTab = false;
    }

    this.setState(updates)
  }

  onLogout = async () => {
    try {
      // Call backend to clear server-side session
      const response = await fetch(`${API_URL}/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.state.csrfToken
        },
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        // Clear frontend state
        this.setState({
          ...initialState,
          route: 'login'
        });

        // Show success toast
        this.showToast({
          title: 'Uitgelogd',
          description: data.message,
          status: 'success',
          duration: 3000,
          isClosable: true
        });
      } else {
        // Even if backend fails, still clear frontend state for UX
        this.setState({
          ...initialState,
          route: 'login'
        });

        console.error('Logout failed:', data.message);
      }
    } catch (error) {
      // Network error - still clear frontend state
      console.error('Logout error:', error);
      this.setState({
        ...initialState,
        route: 'login'
      });
    }
  }

  onGuestLogin = async () => {
    // Fetch CSRF token for guest submissions
    await this.fetchCsrfToken();

    // Immediately go to guest welcome page without email prompt
    this.setState({
      userVersion: 'guest',
      route: 'guest_welcome'
    });
  }

  showToast = (toastOptions) => {
    if (this.props.toast) {
      this.props.toast(toastOptions);
    }
  }

  onOpenFeedback = () => {
    this.setState({ isFeedbackModalOpen: true });
  }

  onCloseFeedback = () => {
    this.setState({ isFeedbackModalOpen: false });
  }

  onOpenAiConsentModal = () => {
    this.setState({ isAiConsentModalOpen: true });
  }

  onCloseAiConsentModal = () => {
    this.setState({
      isAiConsentModalOpen: false,
      hasSeenAiConsentModal: true
    });
  }

  onAiConsentGiven = (consented) => {
    // Mark that user has seen the modal
    this.setState({ hasSeenAiConsentModal: true });

    
  }

  onOnboardingComplete = () => {
    this.setState({ onboardingCompleted: true });

    // Show AI consent modal for Burger users 1.5 seconds after onboarding completes
    if (this.state.userVersion === 'burger' && !this.state.hasSeenAiConsentModal) {
      setTimeout(() => {
        this.onOpenAiConsentModal();
      }, 1500);
    }
  }

  // REMOVED: Users can no longer manually change their version/role
  // Role is now determined by backend on login/register
  // onVersionChange method has been removed for security

  onTrendingTopicFilter = (topic) => {
    this.setState({
      trendingTopicFilter: topic
    }, () => {
      // Load all suggestions with the new filter
      this.loadAllSuggestions().then(() => {
        this.onRouteChange('alleSuggesties');
      });
    })
  }

  onSuggestionTextChange = (text) => {
    this.setState({
      suggestionText: text
    })
  }

  onAiConsentChange = (allowed) => {
    this.setState({
      allowAiSummarization: allowed
    })
  }

  onSubmitSuggestion = async () => {
    if (!this.state.suggestionText || this.state.suggestionText.trim().length === 0) {
      this.props.toast({
        position: 'top',
        title: 'Invoerfout',
        description: 'Voer eerst een suggestie in',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // For guest users, validate email
    if (this.state.userVersion === 'guest') {
      if (!this.state.email || this.state.email.trim().length === 0) {
        this.props.toast({
          position: 'top',
          title: 'Email verplicht',
          description: 'Vul je emailadres in',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.state.email)) {
        this.props.toast({
          position: 'top',
          title: 'Ongeldig emailadres',
          description: 'Vul een geldig emailadres in',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    this.setState({ isSubmittingSuggestion: true });

    // Use different endpoints for guests vs regular users
    const isGuest = this.state.userVersion === 'guest';
    const endpoint = isGuest ? `${API_URL}/submit_guest` : `${API_URL}/submit_suggestion`;

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(
        isGuest
          ? {
              email: this.state.email,
              text: this.state.suggestionText,
              allow_ai_summarization: this.state.allowAiSummarization
            }
          : {
              username: this.state.username,
              text: this.state.suggestionText
            }
      )
    }

    try {
      // Both guest and regular users need CSRF token protection
      const response = await (await this.authenticatedFetch(endpoint, requestOptions)).json();

      let success = response['success']
      let message = response['message']

      if (success === true) {
        this.setState({ isSubmittingSuggestion: false });
        this.props.toast({
          title: 'Succes!',
          position: 'top',
          description: message || 'Suggestie succesvol ingediend!',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        this.setState({
          suggestionText: '',
          email: isGuest ? '' : this.state.email,  // Clear email for guests
          allowAiSummarization: false  // Reset AI consent
        })
        // Redirect - guests go back to welcome, regular users to their suggestions
        if (isGuest) {
          this.onRouteChange('guest_welcome')
        } else {
          this.onRouteChange('ingediendeSuggesties')
        }
      } else {
        this.setState({ isSubmittingSuggestion: false });
        this.props.toast({
          title: 'Fout',
          position: 'top',
          description: message || 'Er is iets misgegaan',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      this.setState({ isSubmittingSuggestion: false });
      this.props.toast({
        title: 'Verbindingsfout',
        position: 'top',
        description: 'Probeer later opnieuw.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }

  loadSuggestions = async () => {
    this.setState({ isLoadingSuggestions: true });

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username: this.state.username,
        page: this.state.suggestions.page
      })
    }

    try {
      let response = await (await this.authenticatedFetch(`${API_URL}/get_suggestions`, requestOptions)).json()
      let success = response['success']
      let result = response['result']

      if (success === true) {
        this.setState({
          isLoadingSuggestions: false,
          suggestions: {
            ...this.state.suggestions,
            items: result,
            hasNext: response['has_next'],
            hasPrev: response['has_prev'],
            total: response['total']
          },
        })
      } else {
        this.setState({ isLoadingSuggestions: false });
      }
    } catch (error) {
      this.setState({ isLoadingSuggestions: false });
      console.error('Error loading suggestions:', error)
      this.props.toast({
        title: 'Fout',
        position: 'top',
        description: 'Kan suggesties niet laden. Probeer het later opnieuw.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }

  loadAllSuggestions = async () => {
    // Load all suggestions without pagination if there's a trending filter active
    const loadAll = this.state.trendingTopicFilter !== null;

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username: this.state.username,  // Required for @require_admin decorator
        page: this.state.allSuggestions.page,
        load_all: loadAll
      })
    }
    
    try {
      let response = await (await this.authenticatedFetch(`${API_URL}/get_all_suggestions`, requestOptions)).json()
      let success = response['success']
      let result = response['result']
      
      if (success === true) {
        this.setState({
          allSuggestions: {
            ...this.state.allSuggestions,
            items: result,
            hasNext: response['has_next'],
            hasPrev: response['has_prev'],
            total: response['total']
          },
        })
      }
    } catch (error) {
      console.error('Error loading all suggestions:', error)
    }
  }

  loadSummaries = async () => {
    try {
      let response = await (await this.authenticatedFetch(`${API_URL}/get_summaries`)).json()
      let success = response['success']
      let result = response['result']

      if (success === true) {
        this.setState({
          summaries: {
            ...this.state.summaries,
            items: result,
            lastUpdated: response['last_updated']
          }
        })
      }
    } catch (error) {
      console.error('Error loading summaries:', error)
    }
  }

  onExpandSummaryDetails = async (summaryId) => {
    try {
      let response = await (await this.authenticatedFetch(`${API_URL}/get_summary_suggestions/${summaryId}`)).json()
      let success = response['success']
      let suggestions = response['suggestions']

      if (success === true && suggestions) {
        // Update the specific summary in the summaries array with sample_suggestions
        this.setState({
          summaries: {
            ...this.state.summaries,
            items: this.state.summaries.items.map(summary =>
              summary.id === summaryId
                ? { ...summary, sample_suggestions: suggestions.map(s => s.text) }
                : summary
            )
          }
        })
      }
    } catch (error) {
      console.error('Error loading summary suggestions:', error)
      this.showToast({
        title: 'Fout',
        description: 'Kan voorbeelden niet laden.',
        status: 'error',
        duration: 3000
      })
    }
  }

  generateSummaries = async () => {
    this.setState({
      summaries: {
        ...this.state.summaries,
        loading: true
      }
    })

    try {
      let response = await (await this.authenticatedFetch(`${API_URL}/generate_summaries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: this.state.username  // Required for @require_admin decorator
        })
      })).json()
      let success = response['success']
      
      if (success === true) {
        // Reload summaries and trending topics after generation
        await this.loadSummaries()
        await this.loadTrendingTopics()
      } else {
        this.props.toast({
          title: 'Samenvattingen niet gegenereerd',
          position: 'top',
          description: 'Kan de samenvattingen niet genereren. Er zijn mogelijk onvoldoende suggesties of de AI-service is tijdelijk niet beschikbaar.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error generating summaries:', error)
      this.props.toast({
        title: 'Verbindingsfout',
        position: 'top',
        description: 'Kan geen verbinding maken met de server. Controleer uw internetverbinding en probeer het later opnieuw.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      this.setState({
        summaries: {
          ...this.state.summaries,
          loading: false
        }
      })
    }
  }

  loadTrendingTopics = async () => {
    try {
      let response = await (await this.authenticatedFetch(`${API_URL}/get_trending_topics`)).json()
      let success = response['success']
      let result = response['result']
      
      if (success === true) {
        this.setState({
          trendingTopics: {
            ...this.state.trendingTopics,
            items: result,
            lastUpdated: response['last_updated']
          }
        })
      }
    } catch (error) {
      console.error('Error loading trending topics:', error)
    }
  }

  loadProblems = async () => {
    this.setState(prevState => ({
      problems: {
        ...prevState.problems,
        loading: true
      }
    }))

    try {
      let response = await (await this.authenticatedFetch(`${API_URL}/get_problems`)).json()
      let success = response['success']
      let result = response['result']

      if (success === true) {
        this.setState(prevState => ({
          problems: {
            ...prevState.problems,
            items: result,
            lastUpdated: response['last_updated'],
            loading: false
          }
        }))
      } else {
        this.setState(prevState => ({
          problems: {
            ...prevState.problems,
            loading: false
          }
        }))
      }
    } catch (error) {
      console.error('Error loading problems:', error)
      this.setState(prevState => ({
        problems: {
          ...prevState.problems,
          loading: false
        }
      }))
    }
  }

  refreshProblems = async () => {
    this.setState(prevState => ({
      problems: {
        ...prevState.problems,
        loading: true
      }
    }))

    try {
      let response = await (await this.authenticatedFetch(`${API_URL}/refresh_problems`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: this.state.username  // Required for @require_admin decorator
        })
      })).json()
      let success = response['success']
      let result = response['result']

      if (success === true) {
        this.setState(prevState => ({
          problems: {
            ...prevState.problems,
            items: result,
            lastUpdated: response['last_updated'],
            loading: false
          }
        }))
      } else {
        this.setState(prevState => ({
          problems: {
            ...prevState.problems,
            loading: false
          }
        }))
        this.props.toast({
          title: 'Problemen niet geanalyseerd',
          position: 'top',
          description: 'Kan de problemen niet analyseren. Er zijn mogelijk onvoldoende suggesties of de AI-service is tijdelijk niet beschikbaar.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        })
      }
    } catch (error) {
      console.error('Error refreshing problems:', error)
      this.setState(prevState => ({
        problems: {
          ...prevState.problems,
          loading: false
        }
      }))
      this.props.toast({
        title: 'Verbindingsfout',
        position: 'top',
        description: 'Kan geen verbinding maken met de server. Probeer het later opnieuw.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    }
  }

  onSuggestionSelect = (suggestion) => {
    this.setState({
      selectedSuggestion: suggestion
    });
    this.onRouteChange('suggestionInfo');
  }

  onSummarySelect = (summary) => {
    this.setState({
      selectedSummary: summary
    });
    this.onRouteChange('summaryInfo');
  }

  onProblemSelect = (problem) => {
    this.setState({
      selectedProblem: problem
    });
    this.onRouteChange('problemInfo');
  }

  onLawSelect = (law) => {
    this.setState({
      selectedLaw: law
    });
    this.onRouteChange('wetDetails');
  }

  onReportSelect = (report) => {
    this.setState({
      selectedReport: report
    });
    this.onRouteChange('rapportDetails');
  }

  onSuggestionStatusUpdate = async (suggestionId, newStatus) => {
    const requestOptions = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username: this.state.username,  // Required for @require_admin decorator
        status: newStatus
      })
    }
    
    try {
      let response = await (await this.authenticatedFetch(`${API_URL}/update_suggestion_status/${suggestionId}`, requestOptions)).json()
      let success = response['success']
      
      if (success === true) {
        // Reload all suggestions to reflect the status change
        this.loadAllSuggestions()
        
        // Update selected suggestion if we're viewing it
        if (this.state.selectedSuggestion && this.state.selectedSuggestion.id === suggestionId) {
          this.setState({
            selectedSuggestion: {
              ...this.state.selectedSuggestion,
              status: newStatus
            }
          })
        }
      } else {
        this.props.toast({
          title: 'Status niet bijgewerkt',
          position: 'top',
          description: 'De status kon niet worden gewijzigd. Controleer uw rechten of probeer het opnieuw.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error updating suggestion status:', error)
      this.props.toast({
        title: 'Verbindingsfout',
        position: 'top',
        description: 'Kan geen verbinding maken met de server. Controleer uw internetverbinding en probeer het opnieuw.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }

  onFormTextChange = (form, key, value) => {
    this.setState({
      [form]: {
        ...this.state[form],
        [key]: value
      }
    })
  }

  onPaginationButtonClick = (value, loc) => {
    // Handle message pagination differently due to nested structure
    if (loc.startsWith('messages_')) {
      const messageType = loc.split('_')[1]; // 'received' or 'sent'
      let page = this.state.messages[messageType].page
      let add = 0
      if (value === 'Next') {
        add += 1
      } else {
        add += -1
      }
      this.setState({
        messages: {
          ...this.state.messages,
          [messageType]: {
            ...this.state.messages[messageType],
            page: page += add
          }
        }
      }, () => {
        this.loadMessages(messageType);
      });
    } else {
      // Handle regular pagination for suggestions
      let page = this.state[loc].page
      let add = 0
      if (value === 'Next') {
        add += 1
      } else {
        add += -1
      }
      this.setState({
        [loc]: {
          ...this.state[loc],
          page: page += add
        }
      }, () => {
        // Reload data after state update
        if (loc === 'suggestions') {
          this.loadSuggestions();
        } else if (loc === 'allSuggestions') {
          this.loadAllSuggestions();
        }
      }) 
    }
  }

  resetSuggestionsPage = () => {
    this.setState({
      suggestions: {
        ...this.state.suggestions,
        page: 0
      }
    });
  }

  // Message-related methods
  onMessageFieldChange = (field, value) => {
    this.setState({
      [field]: value
    });
  }

  onClearSentTabFlag = () => {
    this.setState({
      showSentTab: false
    });
  }

  onSendMessage = async () => {
    if (!this.state.messageRecipient || this.state.messageRecipient.trim().length === 0) {
      this.props.toast({
        position: 'top',
        title: 'Invoerfout',
        description: 'Voer een ontvanger in',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!this.state.messageSubject || this.state.messageSubject.trim().length === 0) {
      this.props.toast({
        position: 'top',
        title: 'Invoerfout',
        description: 'Voer een onderwerp in',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!this.state.messageText || this.state.messageText.trim().length === 0) {
      this.props.toast({
        position: 'top',
        title: 'Invoerfout',
        description: 'Voer een bericht in',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const requestBody = {
      sender_username: this.state.username,
      recipient_username: this.state.messageRecipient.trim(),
      subject: this.state.messageSubject.trim(),
      text: this.state.messageText.trim()
    };
    
    // Include suggestion_id if we're sending a message about a specific suggestion
    if (this.state.messageContext && this.state.messageContext.id) {
      requestBody.suggestion_id = this.state.messageContext.id;
    }
    
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    }
    
    try {
      let response = await (await this.authenticatedFetch(`${API_URL}/send_message`, requestOptions)).json()
      let success = response['success']
      let message = response['message']
      
      if (success === true) {
        this.props.toast({
          title: 'Succes!',
          position: 'top',
          description: message || 'Bericht succesvol verzonden!',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        this.setState({
          messageText: '',
          messageSubject: '',
          messageRecipient: '',
          messageContext: null,
          showSentTab: true // Flag to show sent tab
        })
        // Reload sent messages to show the newly sent message
        this.loadMessages('sent')
        // Redirect to messages list - will show sent tab
        this.onRouteChange('berichten')
      } else {
        this.props.toast({
          title: 'Bericht niet verzonden',
          position: 'top',
          description: message || 'Het bericht kon niet worden verzonden. Controleer of de ontvanger bestaat en probeer het opnieuw.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      this.props.toast({
        title: 'Verbindingsfout',
        position: 'top',
        description: 'Kan geen verbinding maken met de server. Controleer uw internetverbinding en probeer het later opnieuw.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }

  onSendMessageToUser = (username, suggestion = null) => {
    let subject = '';
    let text = '';
    
    if (suggestion) {
      subject = `Vraag over uw suggestie: ${suggestion.text.substring(0, 50)}${suggestion.text.length > 50 ? '...' : ''}`;
      text = `Geachte ${username},\n\nIk heb een vraag over uw ingediende suggestie:\n\n"${suggestion.text}"\n\nKunt u hierover meer details verstrekken?\n\nMet vriendelijke groet,\n\n${this.state.username} `;
    }
    
    this.setState({
      messageRecipient: username,
      messageSubject: subject,
      messageText: text,
      messageContext: suggestion // Store suggestion for context
    });
    this.onRouteChange('berichtSturen');
  }

  loadMessages = async (type = 'received') => {
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username: this.state.username,
        type: type,
        page: this.state.messages[type].page
      })
    }
    
    try {
      let response = await (await this.authenticatedFetch(`${API_URL}/get_messages`, requestOptions)).json()
      let success = response['success']
      
      if (success === true) {
        this.setState({
          messages: {
            ...this.state.messages,
            [type]: {
              items: response.result || [],
              page: response.page || 0,
              hasNext: response.hasNext || false,
              hasPrev: response.hasPrev || false,
              total: response.total || 0
            }
          }
        });
      } else {
        console.error('Failed to load messages:', response.message);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  }

  onMessageSelect = (message, fromTab = 'received') => {
    this.setState({
      selectedMessage: message,
      lastMessageTab: fromTab
    });
    this.onRouteChange('berichtInfo');
  }

  onMarkMessageAsRead = async (messageId) => {
    const requestOptions = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username: this.state.username
      })
    }
    
    try {
      let response = await (await this.authenticatedFetch(`${API_URL}/mark_message_read/${messageId}`, requestOptions)).json()
      let success = response['success']
      
      if (success === true) {
        // Reload messages to reflect the change
        this.loadMessages('received');
        
        // Update selected message if we're viewing it
        if (this.state.selectedMessage && this.state.selectedMessage.id === messageId) {
          this.setState({
            selectedMessage: {
              ...this.state.selectedMessage,
              is_read: true,
              read_at: new Date().toISOString()
            }
          })
        }
      } else {
        this.props.toast({
          title: 'Fout',
          position: 'top',
          description: 'Fout bij markeren als gelezen',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error marking message as read:', error);
      this.props.toast({
        title: 'Verbindingsfout',
        position: 'top',
        description: 'Verbindingsfout bij markeren als gelezen',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }

  onReplyToMessage = (message) => {
    this.setState({
      messageRecipient: message.sender_username,
      messageSubject: message.subject.startsWith('Re: ') ? message.subject : `Re: ${message.subject}`,
      messageText: `\n\n--- Oorspronkelijk bericht ---\nVan: ${message.sender_username}\nOnderwerp: ${message.subject}\nDatum: ${new Date(message.created_at).toLocaleDateString('nl-NL')}\n\n${message.text}`
    });
    this.onRouteChange('berichtSturen');
  }

  onToggleCommenting = async () => {
    this.setState({
      policyInfo: {
        ...this.state.policyInfo,
        comment: {
          commenting: !this.state.policyInfo.comment.commenting,
          text: ""
        }
      }
    })
  }

  onFormTextChangeNested4 = (level_1, level_2, key, value) => {
  //   this.setState({
  //     [level_1]: {
  //       ...this.state[level_1],
  //       [level_2]: {
  //         ...this.state[level_2],
  //         [key]: value
  //       }
  //     }
  //   })
  // }
    this.setState({
      [level_1]: {
        ...this.state[level_1],
        [level_2]: {
          commenting: true,
          [key]: value
        }
      }
    })
  }

  onRegister = async () => {
    // Check if user verified age
    if (!this.state.ageVerified) {
      this.props.toast({
        title: 'Leeftijdsverificatie vereist',
        position: 'top',
        description: 'U moet bevestigen dat u 16 jaar of ouder bent om te registreren.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Check if user agreed to terms
    if (!this.state.agreedToTerms) {
      this.props.toast({
        title: 'Acceptatie vereist',
        position: 'top',
        description: 'Je moet akkoord gaan met de Algemene Voorwaarden en het Privacybeleid om te registreren.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }


    this.setState({ isRegistering: true });

    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',  // CRITICAL: Required to store session cookie from response
        body: JSON.stringify({
          user: this.state.username,
          email: this.state.email,
          password: this.state.password,
          agreedToTerms: this.state.agreedToTerms,
          ageVerified: this.state.ageVerified
        })
      }
      let response = await (await fetch(`${API_URL}/register`, requestOptions)).json()
      let success = response['success']
      let message = response['message']
      if (success === true) {
        let result = response['result']

        // Extract role, CSRF token, and onboarding status from backend response
        const userRole = result.role || 'burger';
        const csrfToken = result.csrf_token;
        const onboardingCompleted = result.onboarding_completed !== undefined ? result.onboarding_completed : false;
        const welcomeRoute = userRole === 'vertegenwoordiger' ? 'dashboard' : 'burgerWelcome';

        this.props.toast({
          title: 'Succes!',
          position: 'top',
          description: message,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });

        // Set all state including route in single setState to avoid race condition
        this.setState({
          ...this.state,
          isRegistering: false,
          username: result.username,  // Ensure username is set
          email: result.email,  // Ensure email is set
          userVersion: userRole,  // Set role from backend
          csrfToken: csrfToken,  // Set CSRF token from registration response
          onboardingCompleted: onboardingCompleted,  // Set onboarding status (false for new users)
          feed: {
            feedItems: result,
            page: 0
          },
          route: welcomeRoute  // Set route in same setState to avoid race condition
        }, () => {
          // After state is set, check if we should show AI consent modal for Burger users
          // Only show if user is Burger AND hasn't been prompted before (from backend) AND onboarding is completed
          const aiConsentPrompted = result.ai_consent_prompted || false;
          if (userRole === 'burger' && !aiConsentPrompted && !this.state.hasSeenAiConsentModal && onboardingCompleted) {
            // Show modal after a short delay to let the welcome page load first
            setTimeout(() => {
              this.onOpenAiConsentModal();
            }, 1500);
          }
        })
      } else {
        this.setState({ isRegistering: false });
        this.props.toast({
          title: 'Fout',
          position: 'top',
          description: message,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      this.setState({ isRegistering: false });
      this.props.toast({
        title: 'Fout',
        position: 'top',
        description: 'Kan geen verbinding maken met de server. Probeer het later opnieuw.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }

  checkSignIn = async () => {
    this.setState({ isLoggingIn: true });

    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',  // CRITICAL: Required to store session cookie from response
        body: JSON.stringify({
          user: this.state.username,
          password: this.state.password
        })
      }
      let response = await (await fetch(`${API_URL}/check_signin`, requestOptions)).json()
      let success = response['success']
      let message = response['message']
      if (success === true) {
        let result = response['result']
        
        // Extract role, CSRF token, email, and onboarding status from backend response
        const userRole = result.role || 'burger';
        const csrfToken = result.csrf_token;
        const userEmail = result.email || '';
        const onboardingCompleted = result.onboarding_completed !== undefined ? result.onboarding_completed : true;
        const welcomeRoute = userRole === 'vertegenwoordiger' ? 'dashboard' : 'burgerWelcome';

        this.props.toast({
          title: 'Welkom terug!',
          position: 'top',
          description: message,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });

        // First set CSRF token and user info without changing route
        this.setState({
          ...this.state,
          isLoggingIn: false,
          username: result.username,  // Ensure username is set
          email: userEmail,  // Set email from backend
          userVersion: userRole,  // Set role from backend
          csrfToken: csrfToken,  // Set CSRF token from login response
          onboardingCompleted: onboardingCompleted,  // Set onboarding status from backend
          feed: {
            feedItems: result,
            page: 0
          },
          route: welcomeRoute  // Set route in same setState to avoid race condition
        }, () => {
          // After state is set, check if we should show AI consent modal for Burger users
          // Only show if user is Burger AND hasn't been prompted before (from backend) AND onboarding is completed
          const aiConsentPrompted = result.ai_consent_prompted || false;
          if (userRole === 'burger' && !aiConsentPrompted && !this.state.hasSeenAiConsentModal && onboardingCompleted) {
            // Show modal after a short delay to let the welcome page load first
            setTimeout(() => {
              this.onOpenAiConsentModal();
            }, 1500);
          }
        })
      } else {
        this.setState({ isLoggingIn: false });
        this.props.toast({
          title: 'Fout',
          position: 'top',
          description: message,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      this.setState({ isLoggingIn: false });
      this.props.toast({
        title: 'Fout',
        position: 'top',
        description: 'Kan geen verbinding maken met de server. Probeer het later opnieuw.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }

  getFeed = async () => {
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username: this.state.username
      })
    }
    let response = await (await this.authenticatedFetch(`${API_URL}/get_feed`, requestOptions)).json()
    let success = response['success']
    let result = response['result']
    if (success === true) {
      this.setState({
        route: 'burgerWelcome',
        feed: {
          ...this.state.feed,
        feedItems: result,
        },
      })
    }
  }

  onVotePolicy = async (policy, vote) => {
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        user: this.state.username,
        policy: policy,
        vote: vote
      })
    }
    let response = await (await this.authenticatedFetch(`${API_URL}/vote_policy`, requestOptions)).json()
    let result = response['result']
    this.setState({
      ...this.state,
      feed: {
        ...this.state.feed,
        feedItems: result,
      },
    })
  }

  onUnvotePolicy = async (policy) => {
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username: this.state.username,
        policy: policy
      })
    }
    let response = await (await this.authenticatedFetch(`${API_URL}/unvote_policy`, requestOptions)).json()
    let result = response['result']
    this.setState({
      ...this.state,
      feed: {
        ...this.state.feed,
        feedItems: result,
      },
    })
  }

  postPolicy = async () => {
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        policyDetails: this.state.policyDetails
      })
    }
    let response = await (
      await this.authenticatedFetch(`${API_URL}/add_policy`, requestOptions)
    ).json()
    let success = response['success']
    let message = response['message']
    if (success === 'true') {
      this.props.toast({
        title: 'Succes!',
        position: 'top',
        description: message,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      this.setState({
        ...this.state,
        policyDetails: initialPolicyDetails,
      })
    } else {
      this.props.toast({
        title: 'Fout',
        position: 'top',
        description: message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }

  onPolicyInfo = async (id) => {
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username: this.state.username,
        policyId: id
      })
    }
    let response = await (
      await this.authenticatedFetch(`${API_URL}/policy_info`, requestOptions)
    ).json()
    let success = response['success']
    let policy = response['policy']
    let comments = response['comments']
    if (success === true) {
      this.setState({
        ...this.state,
        route: 'policyInfo',
        policyInfo: {
          policy: policy,
          comment: {
            commenting: false,
            text: ""
          },
          comments: comments,
          page: 0
        }
      })
    } else {
      this.props.toast({
        title: 'Fout',
        position: 'top',
        description: 'Er is iets misgegaan',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }

  postComment = async (id) => {
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        policyId: id,
        username: this.state.username,
        text: this.state.policyInfo.comment.text
      })
    }
    let response = await (
      await this.authenticatedFetch(`${API_URL}/post_comment`, requestOptions)
    ).json()
    let success = response['success']
    let message = response['message']
    let comments = response['comments']
    if (success === true) {
      this.props.toast({
        title: 'Succes!',
        position: 'top',
        description: message,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      this.setState({
       policyInfo: {
        ...this.state.policyInfo,
        comment: initialComment,
        comments: comments
       } 
      })
    } else {
      this.props.toast({
        title: 'Fout',
        position: 'top',
        description: 'Er is iets misgegaan',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }

  onAgreeComment = async (policyId, commentId) => {
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username: this.state.username,
        policyId: policyId,
        commentId: commentId
      })
    }
    let response = await (
      await this.authenticatedFetch(`${API_URL}/agree_comment`, requestOptions)
    ).json()
    let success = response['success']
    let comments = response['comments']
    if (success === true) {
      this.setState({
       policyInfo: {
        ...this.state.policyInfo,
        comment: initialComment,
        comments: comments
       } 
      })
    } else {
      this.props.toast({
        title: 'Fout',
        position: 'top',
        description: 'Er is iets misgegaan',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }

  onUnagreeComment = async (policyId, commentId) => {
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username: this.state.username,
        policyId: policyId,
        commentId: commentId
      })
    }
    let response = await (
      await this.authenticatedFetch(`${API_URL}/unagree_comment`, requestOptions)
    ).json()
    let success = response['success']
    let comments = response['comments']
    if (success === true) {
      this.setState({
       policyInfo: {
        ...this.state.policyInfo,
        comment: initialComment,
        comments: comments
       } 
      })
    } else {
      this.props.toast({
        title: 'Fout',
        position: 'top',
        description: 'Er is iets misgegaan',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }

  render() {
    return (
      <div className="app">
      {/* Offline Detection Banner - Shows when backend is unreachable */}
      <OfflineBanner />

      {/* Cookie Consent Banner - Shows on all pages */}
      <CookieConsent
        onRouteChange={this.onRouteChange}
        username={this.state.username}
        authenticatedFetch={this.authenticatedFetch}
      />

      {this.state.route === 'landing'
        ?
        <div className = "landing">
          <LandingPage
          onRouteChange = {this.onRouteChange}
          />
        </div>
        : this.state.route === 'privacyPolicy'
        ?
        <div className = "privacyPolicy">
          <PrivacyPolicy
          onRouteChange = {this.onRouteChange}
          />
        </div>
        : this.state.route === 'termsOfService'
        ?
        <div className = "termsOfService">
          <TermsOfService
          onRouteChange = {this.onRouteChange}
          />
        </div>
        : this.state.route === 'suggestieIndienen' || this.state.route === 'indienen'
        ?
         <div class = "suggestieIndienen">
           <SuggestieIndienen
           state = {this.state}
           onRouteChange = {this.onRouteChange}
           getFeed = {this.getFeed}
           onVersionChange = {this.onVersionChange}
           onLogout = {this.onLogout}
           onOpenFeedback = {this.onOpenFeedback}
           onEmailChange = {this.onEmailChange}
           onSuggestionTextChange = {this.onSuggestionTextChange}
           onAiConsentChange = {this.onAiConsentChange}
           onSubmitSuggestion = {this.onSubmitSuggestion}
           resetSuggestionsPage = {this.resetSuggestionsPage}
           />
         </div>
        : this.state.route === 'ingediendeSuggesties'
        ?
         <div class = "ingediendeSuggesties">
           <IngediendeSuggesties
           state = {this.state}
           onRouteChange = {this.onRouteChange}
           getFeed = {this.getFeed}
           onVersionChange = {this.onVersionChange}
           onLogout = {this.onLogout}
           onOpenFeedback = {this.onOpenFeedback}
           loadSuggestions = {this.loadSuggestions}
           onPaginationButtonClick = {this.onPaginationButtonClick}
           resetSuggestionsPage = {this.resetSuggestionsPage}
           />
         </div>
        : this.state.route === 'alleSuggesties'
        ?
         <div class = "alleSuggesties">
           <AlleSuggesties
           state = {this.state}
           onRouteChange = {this.onRouteChange}
           getFeed = {this.getFeed}
           onVersionChange = {this.onVersionChange}
           onLogout = {this.onLogout}
           onOpenFeedback = {this.onOpenFeedback}
           loadAllSuggestions = {this.loadAllSuggestions}
           onPaginationButtonClick = {this.onPaginationButtonClick}
           onSuggestionStatusUpdate = {this.onSuggestionStatusUpdate}
           onSuggestionSelect = {this.onSuggestionSelect}
           onSendMessageToUser = {this.onSendMessageToUser}
           onClearTrendingFilter = {() => this.setState({ trendingTopicFilter: null })}
           resetSuggestionsPage = {this.resetSuggestionsPage}
           />
         </div>
        : this.state.route === 'overzicht'
        ?
         <div class = "overzicht">
           <Overzicht
           state = {this.state}
           onRouteChange = {this.onRouteChange}
           getFeed = {this.getFeed}
           onVersionChange = {this.onVersionChange}
           onLogout = {this.onLogout}
           onOpenFeedback = {this.onOpenFeedback}
           loadSummaries = {this.loadSummaries}
           generateSummaries = {this.generateSummaries}
           loadTrendingTopics = {this.loadTrendingTopics}
           loadProblems = {this.loadProblems}
           onTrendingTopicFilter = {this.onTrendingTopicFilter}
           onSummarySelect = {this.onSummarySelect}
           onProblemSelect = {this.onProblemSelect}
           onExpandSummaryDetails = {this.onExpandSummaryDetails}
           resetSuggestionsPage = {this.resetSuggestionsPage}
           authenticatedFetch = {this.authenticatedFetch}
           toast = {this.props.toast}
           showToast = {this.showToast}
           />
         </div>
        : this.state.route === 'suggestionInfo'
        ?
         <div class = "suggestionInfo">
           <SuggestionInfo
           state = {this.state}
           onRouteChange = {this.onRouteChange}
           getFeed = {this.getFeed}
           onVersionChange = {this.onVersionChange}
           onLogout = {this.onLogout}
           onOpenFeedback = {this.onOpenFeedback}
           onSuggestionStatusUpdate = {this.onSuggestionStatusUpdate}
           onSendMessageToUser = {this.onSendMessageToUser}
           resetSuggestionsPage = {this.resetSuggestionsPage}
           authenticatedFetch = {this.authenticatedFetch}
           showToast = {this.showToast}
           />
         </div>
        : this.state.route === 'berichtSturen'
        ?
         <div class = "berichtSturen">
           <BerichtSturen
           state = {this.state}
           onRouteChange = {this.onRouteChange}
           getFeed = {this.getFeed}
           onVersionChange = {this.onVersionChange}
           onLogout = {this.onLogout}
           onOpenFeedback = {this.onOpenFeedback}
           onMessageFieldChange = {this.onMessageFieldChange}
           onSendMessage = {this.onSendMessage}
           />
         </div>
        : this.state.route === 'berichten'
        ?
         <div class = "berichten">
           <Berichten
           state = {this.state}
           onRouteChange = {this.onRouteChange}
           getFeed = {this.getFeed}
           onVersionChange = {this.onVersionChange}
           onLogout = {this.onLogout}
           onOpenFeedback = {this.onOpenFeedback}
           loadMessages = {this.loadMessages}
           onMessageSelect = {this.onMessageSelect}
           onMarkMessageAsRead = {this.onMarkMessageAsRead}
           onPaginationButtonClick = {this.onPaginationButtonClick}
           onClearSentTabFlag = {this.onClearSentTabFlag}
           />
         </div>
        : this.state.route === 'berichtInfo'
        ?
         <div class = "berichtInfo">
           <BerichtInfo
           state = {this.state}
           onRouteChange = {this.onRouteChange}
           getFeed = {this.getFeed}
           onVersionChange = {this.onVersionChange}
           onLogout = {this.onLogout}
           onOpenFeedback = {this.onOpenFeedback}
           onMarkMessageAsRead = {this.onMarkMessageAsRead}
           onReplyToMessage = {this.onReplyToMessage}
           />
         </div>
        : this.state.route === 'summaryInfo'
        ?
         <div class = "summaryInfo">
           <SummaryInfo
           state = {this.state}
           onRouteChange = {this.onRouteChange}
           getFeed = {this.getFeed}
           onVersionChange = {this.onVersionChange}
           onLogout = {this.onLogout}
           onOpenFeedback = {this.onOpenFeedback}
           resetSuggestionsPage = {this.resetSuggestionsPage}
           authenticatedFetch = {this.authenticatedFetch}
           toast = {this.showToast}
           />
         </div>
        : this.state.route === 'problemInfo'
        ?
         <div class = "problemInfo">
           <ProblemInfo
           state = {this.state}
           onRouteChange = {this.onRouteChange}
           getFeed = {this.getFeed}
           onVersionChange = {this.onVersionChange}
           onLogout = {this.onLogout}
           onOpenFeedback = {this.onOpenFeedback}
           resetSuggestionsPage = {this.resetSuggestionsPage}
           onSuggestionSelect = {this.onSuggestionSelect}
           authenticatedFetch = {this.authenticatedFetch}
           toast = {this.showToast}
           />
         </div>
        : this.state.route === 'burgerWelcome'
        ?
         <div class = "burgerWelcome">
           <BurgerWelcome
           state = {this.state}
           onRouteChange = {this.onRouteChange}
           getFeed = {this.getFeed}
           onVersionChange = {this.onVersionChange}
           onLogout = {this.onLogout}
           onOpenFeedback = {this.onOpenFeedback}
           loadSuggestions = {this.loadSuggestions}
           resetSuggestionsPage = {this.resetSuggestionsPage}
           onOnboardingComplete = {this.onOnboardingComplete}
           authenticatedFetch = {this.authenticatedFetch}
           />
         </div>
        : this.state.route === 'guest_welcome'
        ?
         <div class = "guestWelcome">
           <GuestWelcome
           state = {this.state}
           onRouteChange = {this.onRouteChange}
           getFeed = {this.getFeed}
           onVersionChange = {this.onVersionChange}
           onLogout = {this.onLogout}
           onOpenFeedback = {this.onOpenFeedback}
           resetSuggestionsPage = {this.resetSuggestionsPage}
           authenticatedFetch = {this.authenticatedFetch}
           />
         </div>
        : this.state.route === 'vertegenwoordigerWelcome'
        ?
         <div class = "vertegenwoordigerWelcome">
           <VertegenwoordigerWelcome
           state = {this.state}
           onRouteChange = {this.onRouteChange}
           getFeed = {this.getFeed}
           onVersionChange = {this.onVersionChange}
           onLogout = {this.onLogout}
           onOpenFeedback = {this.onOpenFeedback}
           loadAllSuggestions = {this.loadAllSuggestions}
           loadSummaries = {this.loadSummaries}
           resetSuggestionsPage = {this.resetSuggestionsPage}
           />
         </div>
        : this.state.route === 'dashboard'
        ?
         <div class = "dashboard">
           <Dashboard
           state = {this.state}
           onRouteChange = {this.onRouteChange}
           getFeed = {this.getFeed}
           onVersionChange = {this.onVersionChange}
           onLogout = {this.onLogout}
           onOpenFeedback = {this.onOpenFeedback}
           loadAllSuggestions = {this.loadAllSuggestions}
           loadSummaries = {this.loadSummaries}
           loadTrendingTopics = {this.loadTrendingTopics}
           loadProblems = {this.loadProblems}
           onProblemSelect = {this.onProblemSelect}
           generateSummaries = {this.generateSummaries}
           onTrendingTopicFilter = {this.onTrendingTopicFilter}
           onSuggestionSelect = {this.onSuggestionSelect}
           resetSuggestionsPage = {this.resetSuggestionsPage}
           authenticatedFetch = {this.authenticatedFetch}
           toast = {this.props.toast}
           />
         </div>
        
        : this.state.route === 'instellingen'
        ?
         <div class = "instellingen">
           <Instellingen
           state = {this.state}
           onRouteChange = {this.onRouteChange}
           getFeed = {this.getFeed}
           userVersion = {this.state.userVersion}
           onVersionChange = {this.onVersionChange}
           username = {this.state.username}
           showToast = {this.showToast}
           onLogout = {this.onLogout}
           onOpenFeedback = {this.onOpenFeedback}
           resetSuggestionsPage = {this.resetSuggestionsPage}
           authenticatedFetch = {this.authenticatedFetch}
           onEmailChange = {this.onEmailChange}
           />
         </div>
        : this.state.route === 'passwordResetRequest'
         ?
          <div class = "passwordResetRequest">
            <PasswordResetRequest
            onRouteChange = {this.onRouteChange}
            />
          </div>
        : this.state.route === 'passwordResetConfirm'
         ?
          <div class = "passwordResetConfirm">
            <PasswordResetConfirm
            onRouteChange = {this.onRouteChange}
            />
          </div>
        : this.state.route === 'login'
         ?
          <div class = "login">
            <SignIn
            onNameChange = {this.onNameChange}
            onEmailChange = {this.onEmailChange}
            onPasswordChange = {this.onPasswordChange}
            onTermsChange = {this.onTermsChange}
            onAgeVerifiedChange = {this.onAgeVerifiedChange}
            onRegister = {this.onRegister}
            checkSignIn = {this.checkSignIn}
            onGuestLogin = {this.onGuestLogin}
            onRouteChange = {this.onRouteChange}
            state = {this.state}
            />
          </div>
          :
            <div class = "register">
              <SignIn
              onNameChange = {this.onNameChange}
              onEmailChange = {this.onEmailChange}
              onPasswordChange = {this.onPasswordChange}
              onTermsChange = {this.onTermsChange}
              onAgeVerifiedChange = {this.onAgeVerifiedChange}
              onRegister = {this.onRegister}
              checkSignIn = {this.checkSignIn}
              onGuestLogin = {this.onGuestLogin}
              onRouteChange = {this.onRouteChange}
              state = {this.state}
              />
            </div>
            }

        {/* Feedback Modal - Available for all logged-in users */}
        <FeedbackModal
          isOpen={this.state.isFeedbackModalOpen}
          onClose={this.onCloseFeedback}
          csrfToken={this.state.csrfToken}
          showToast={this.showToast}
          authenticatedFetch={this.authenticatedFetch}
        />

        {/* AI Consent Modal - Shown to Burger users after login/registration */}
        <AiConsentModal
          isOpen={this.state.isAiConsentModalOpen}
          onClose={this.onCloseAiConsentModal}
          onConsent={this.onAiConsentGiven}
          username={this.state.username}
          showToast={this.showToast}
          authenticatedFetch={this.authenticatedFetch}
        />
        </div>
    )
  }
}

// Create the wrapped component with toast functionality
const AppUIWithToast = withToast(AppUI);

export default App;